
import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import {
  useCreateProfile,
  useUpdateProfile,
  AcceptedCommodity,
  cropProfileAcceptedCommodityTypes,
  CreateCustomerCropProfileValidation,
  UpdateCustomerCropProfileValidation,
  RiskCropProfile
} from '../../lib/api/risk/profiles';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { BearDecimalInput, BearIntInput } from '@/lib/form/ui';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Loader2, Plus } from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { PermissionType, PossiblePackage } from "@lhc/permissions/stytch_original";
import { useStytchMember } from "@stytch/react/dist/b2b";
import { hasAccessToFeature } from "@/lib/models/auth";

// Main CropProfileForm component
export interface CropProfileFormProps {
  isOpen: boolean;
  onClose: () => void;
  isEdit?: boolean;
  initialData?: Partial<RiskCropProfile> | null;
  customerId?: string;
  customerOptions: { value: string; label: string }[]
  onSuccess?: () => void;
}

export function CropProfileForm({
  isOpen,
  onClose,
  isEdit = false,
  initialData = null,
  customerOptions,
  customerId,
  onSuccess
}: CropProfileFormProps) {
  // const { member } = useStytchMember();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const createProfile = useCreateProfile();
  const updateProfile = useUpdateProfile();

  // Define the form schema based on whether it's create or edit mode
  const formSchema = isEdit
    ? UpdateCustomerCropProfileValidation
    : CreateCustomerCropProfileValidation;

  // Define the type for our form values
  type FormValues = z.infer<typeof formSchema>;

  // Initialize the form with the correct schema and default values
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: initialData ? {
      ...initialData,
      commodity_name: initialData.commodity_name as any,
      acreage: initialData.acreage?.toString() || '',
      projected_yield: initialData.projected_yield?.toString() || '',
      actual_yield: initialData.actual_yield?.toString() || '',
      cost_per_acre: initialData.cost_per_acre?.toString() || '',
      is_enabled: initialData.is_enabled !== undefined ? initialData.is_enabled : true,
    } : {
      customer_id: customerId || '',
      commodity_name: '' as any,
      crop_year: new Date().getFullYear(),
      acreage: '',
      projected_yield: '',
      actual_yield: '',
      cost_per_acre: '',
      is_enabled: true,
    },
  });

  // Update form values when initialData changes
  useEffect(() => {
    if (initialData && isEdit) {
      form.reset({
        ...initialData,
        commodity_name: initialData.commodity_name as any,
        acreage: initialData.acreage?.toString() || '',
        projected_yield: initialData.projected_yield?.toString() || '',
        actual_yield: initialData.actual_yield?.toString() || '',
        cost_per_acre: initialData.cost_per_acre?.toString() || '',
        is_enabled: initialData.is_enabled !== undefined ? initialData.is_enabled : true,
      });
    }
  }, [initialData, isEdit, form]);

  // Update customer ID when it changes from props
  useEffect(() => {
    if (customerId) {
      form.setValue('customer_id', customerId);
    }
  }, [customerId, form]);

  // const hasPermission = hasAccessToFeature({
  //   name: "Risk Write",
  //   requiredPermissions: [PermissionType.Write, PermissionType.Update, PermissionType.Read],
  //   requiredPackages: [PossiblePackage.RiskManagementCropProfile],
  // }, member);

  // if (hasPermission) {
  //   return (<div></div>)
  // }

  // Handle form submission
  const onSubmit = async (values: FormValues) => {
    setIsSubmitting(true);
    try {
      if (isEdit && initialData?.id) {
        const parseResult = UpdateCustomerCropProfileValidation.safeParse({
          ...values,
          acreage: typeof values.acreage === "object" ? values.acreage?.toString() : values.acreage,
          projected_yield: typeof values.projected_yield === "object" ? values.projected_yield?.toString() : values.projected_yield,
          actual_yield: typeof values.actual_yield === "object" ? values.actual_yield?.toString() : values.actual_yield,
          cost_per_acre: typeof values.cost_per_acre === "object" ? values.cost_per_acre?.toString() : values.cost_per_acre,
        });
        if (!parseResult.success) {
          console.warn("edit parse result error", parseResult.error);
          toast({
            title: "Invalid Profile"
          })
          return;
        }

        const result = await updateProfile(initialData.id, parseResult.data);
        if (result.success) {
          toast({ title: 'Profile updated successfully' });
          if (onSuccess) onSuccess();
          onClose();
        } else {
          toast({ title: 'Failed to update profile' });
        }
      } else {
        const parseResult = CreateCustomerCropProfileValidation.safeParse({
          ...values,
          acreage: typeof values.acreage === "object" ? values.acreage?.toString() : values.acreage,
          projected_yield: typeof values.projected_yield === "object" ? values.projected_yield?.toString() : values.projected_yield,
          actual_yield: typeof values.actual_yield === "object" ? values.actual_yield?.toString() : values.actual_yield,
          cost_per_acre: typeof values.cost_per_acre === "object" ? values.cost_per_acre?.toString() : values.cost_per_acre,
        });
        if (!parseResult.success) {
          console.warn("create parse result error", parseResult.error);
          toast({
            title: "Invalid Profile"
          })
          return;
        }
        console.warn("create parse result", values, "||", parseResult.data);
        const result = await createProfile(parseResult.data);
        if (result.success) {
          toast({ title: 'Profile created successfully' });
          if (onSuccess) onSuccess();
          onClose();
        } else if (/profile already exists/img.test(result.message || "")) {
          toast({ title: 'Profile Already Exists', description: "Please refreshing and update the profile" });
        } else {
          toast({ title: 'Failed to create profile' });
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({ title: 'An error occurred while saving the profile' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const customerMessage = customerId ? ['for', customerOptions.find(option => option.value === customerId)?.label || ""].join(" ") : "";

  // Generate customer options for combobox
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? 'Edit Crop Profile' : 'Create New Crop Profile'}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? `Update the details of this crop profile ${customerMessage}.`.trim()
              : `Enter the details of this new crop profile ${customerMessage}.`.trim()}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Customer Selection - only shown when no customerId is provided */}
            {!customerId && (
              <FormField
                control={form.control}
                name="customer_id"
                render={({ field }) => (
                  <FormItem className="grid">
                    <FormLabel>Customer</FormLabel>
                    <FormControl>
                      <Combobox
                        isModal
                        virtualizeList
                        dwimOnChange
                        options={customerOptions}
                        value={field.value}
                        onValueChange={field.onChange}
                        placeholder="Select a customer"
                        disabled={!!customerId || isSubmitting}
                        className="w-full"
                        popoverClassName='md:min-w-[300px] lg:min-w-[400px]'
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Commodity */}
            <FormField
              control={form.control}
              name="commodity_name"
              render={({ field }) => (
                <FormItem className="grid">
                  <FormLabel>Commodity</FormLabel>
                  <FormControl>
                    <Combobox
                      isModal
                      virtualizeList
                      dwimOnChange
                      options={cropProfileAcceptedCommodityTypes.sort().map(c => ({ value: c, label: /wheat/i.test(c) ? "Wheat" : c }))}
                      value={field.value}
                      onValueChange={field.onChange}
                      placeholder="Select a commodity"
                      disabled={isSubmitting}
                      className="w-full"
                      popoverClassName='md:min-w-[300px] lg:min-w-[400px]'
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Crop Year */}
            <FormField
              control={form.control}
              name="crop_year"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Crop Year</FormLabel>
                  <FormControl>
                    <BearIntInput
                      name="Crop Year"
                      value={field.value || 0}
                      onChange={field.onChange}
                      min="2000"
                      placeholder="Enter crop year"
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Acreage */}
            <FormField
              control={form.control}
              name="acreage"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Acreage</FormLabel>
                  <FormControl>
                    <BearDecimalInput
                      name="Acreage"
                      value={field.value as any}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>Total planted acres for this crop</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Projected Yield */}
            <FormField
              control={form.control}
              name="projected_yield"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Projected Yield</FormLabel>
                  <FormControl>
                    <BearDecimalInput
                      name="Projected Yield"
                      value={field.value as any}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>Expected yield per acre</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Actual Yield */}
            <FormField
              control={form.control}
              name="actual_yield"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Actual Yield</FormLabel>
                  <FormControl>
                    <BearDecimalInput
                      name="Actual Yield"
                      value={field.value as any}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>Realized yield per acre</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Cost Per Acre */}
            <FormField
              control={form.control}
              name="cost_per_acre"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Cost Per Acre</FormLabel>
                  <FormControl>
                    <BearDecimalInput
                      name="Cost Per Acre"
                      value={field.value as any}
                      onChange={field.onChange}
                      disabled={isSubmitting}
                    />
                  </FormControl>
                  <FormDescription>Cost per acre for this crop</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Enabled Status - Only shown in edit mode */}
            {isEdit && (
              <FormField
                control={form.control}
                name="is_enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={isSubmitting}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Show Profile</FormLabel>
                      <FormDescription>
                        When disabled, this profile will be hidden and ignored for totaling.
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isEdit ? 'Update Profile' : 'Create Profile'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// Create Profile Dialog Component
export interface CreateCropProfileDialogProps {
  customerId?: string;
  onSuccess?: () => void;
  customerOptions: { value: string; label: string }[];
  useSmallTriggerVariant?: boolean;
  initialData?: Partial<RiskCropProfile> | null;
}

export function CreateCropProfileDialog({ customerOptions, customerId, onSuccess, useSmallTriggerVariant, initialData }: CreateCropProfileDialogProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  return (
    <>
      <Button onClick={handleOpen} className="gap-2">
        <Plus className="h-4 w-4" />
        {!useSmallTriggerVariant && ("Create Crop Profile")}
      </Button>
      {isOpen && (
        <CropProfileForm
          isOpen={isOpen}
          onClose={handleClose}
          customerId={customerId}
          initialData={initialData}
          onSuccess={onSuccess}
          customerOptions={customerOptions}
        />
      )}
    </>
  );
}

// Edit Profile Dialog Component
export interface EditCropProfileDialogProps {
  profile: Partial<RiskCropProfile>;
  onSuccess?: () => void;
  onClose?: () => void;
  trigger?: React.ReactNode;
  customerOptions: { value: string; label: string }[]
}

export function EditCropProfileDialog({ customerOptions, profile, onSuccess, trigger, onClose }: EditCropProfileDialogProps) {
  const handleOpen = () => { };
  const handleClose = () => onClose?.();

  return (
    <>
      <CropProfileForm
        isOpen
        onClose={handleClose}
        isEdit={true}
        initialData={profile}
        customerId={profile.customer_id}
        onSuccess={onSuccess}
        customerOptions={customerOptions}
      />
    </>
  );
}

export default CropProfileForm;
